























































































































import { Component, Vue } from 'vue-property-decorator'
import { Share, ShareType } from '@/api/model/share'
import { ShareAPI } from '@/api/backend-client'
import { OrderType, orderTypes } from '@/api/types'
import { Pipeline } from '@/api/model/pipeline'
import { v4 as uuid } from 'uuid'

import { namespace } from 'vuex-class'
const referrals = namespace('referrals')

@Component
export default class SharesEditShares extends Vue {
    @referrals.State readonly pipelines!: Pipeline[]
    @referrals.Action readonly loadPipelines!: () => Promise<void>
    loading: boolean = false
    shares: Share[] = []

    async mounted(): Promise<void> {
        await this.loadContent()
    }

    findOrderShare(shareType: string, orderType: string): Share|undefined {
        return this.shares.find(share =>
            share.type === shareType &&
            share.orderType === orderType)
    }

    readOrderShare(shareType: string, orderType: string): string {
        return this.findOrderShare(shareType, orderType)?.percentage || '0'
    }

    updateOrderShare(shareType: string, orderType: string, value: string): void {
        const share = this.findOrderShare(shareType, orderType)
        if (share) {
            share.percentage = value
        }
    }

    findPipelineShare(shareType: string, pipelineId: string): Share|undefined {
        return this.shares.find(share =>
            share.type === shareType &&
            share.referralPipeline?.id === pipelineId)
    }

    readPipelineShare(shareType: string, pipelineId: string): string {
        return this.findPipelineShare(shareType, pipelineId)?.percentage || '0'
    }

    updatePipelineShare(shareType: string, pipelineId: string, value: string) {
        const share = this.findPipelineShare(shareType, pipelineId)
        if (share) {
            share.percentage = value
        }
    }

    async loadContent(): Promise<void> {
        this.loading = true
        await this.loadPipelines()
        await this.loadShares()
        await this.ensureSharesAreCreated()
        this.loading = false
    }

    async loadShares(): Promise<void> {
        this.loading = true
        this.shares = await ShareAPI.getAll()
        this.loading = false
    }

    async saveShares(): Promise<void> {
        this.loading = true
        await Promise.all(this.shares.map(share => ShareAPI.update(share.id, share)))
        await this.loadShares()
        this.loading = false
    }

    async createShare(type: ShareType, orderType: OrderType|null, pipelineId: string|null, defaultPercentage: string = '20'): Promise<void> {
        const pipeline = this.pipelines.find(v => v.id === pipelineId)
        const share = new Share(
            this.$currentCompany,
            uuid(),
            type,
            orderType,
            null,
            pipeline ? pipeline : null,
            defaultPercentage,
        )

        await ShareAPI.create(share)
    }

    async ensureSharesAreCreated(): Promise<void> {
        this.loading = true
        let hasChange = false

        switch (this.ordersEnabled) {
            case true: {
                hasChange = await this.ensureOrderSharesAreCreated()
                break
            }

            case false: {
                hasChange = await this.ensurePipelineSharesAreCreated()
                break
            }
        }

        if (hasChange) {
            await this.loadShares()
        }

        this.loading = false
    }

    async ensureOrderSharesAreCreated(): Promise<boolean> {
        let hasChange = false

        for (const orderType of this.filteredOrderTypes) {
            const hasInternal = this.shares.some(share => share.type === ShareType.INTERNAL && share.orderType === orderType)
            const hasExternal = this.shares.some(share => share.type === ShareType.EXTERNAL && share.orderType === orderType)

            if (!hasInternal) {
                await this.createShare(ShareType.INTERNAL, orderType, null)
                hasChange = true
            }

            if (!hasExternal) {
                await this.createShare(ShareType.EXTERNAL, orderType, null)
                hasChange = true
            }
        }

        return hasChange
    }

    async ensurePipelineSharesAreCreated(): Promise<boolean> {
        let hasChange = false

        for (const pipeline of this.pipelines) {
            const hasInternal = this.shares.some(share => share.referralPipeline?.id === pipeline.id && share.type === 'internal')
            const hasExternal = this.shares.some(share => share.referralPipeline?.id === pipeline.id && share.type === 'external')

            if (!hasInternal) {
                await this.createShare(ShareType.INTERNAL, null, pipeline.id)
                hasChange = true
            }

            if (!hasExternal) {
                await this.createShare(ShareType.EXTERNAL, null, pipeline.id)
                hasChange = true
            }
        }

        return hasChange
    }

    get filteredOrderTypes(): OrderType[] {
        return orderTypes().filter(orderType => {
          return this.$currentCompany.orderSettings.enabledTypes
              .includes(orderType)
        })
    }

    get availableOrderTypes(): Array<{ label: string, value: string }> {
        return this.filteredOrderTypes.map(orderType => {
            return {
                label: this.$t(`order.type.${orderType}`).toString(),
                value: orderType,
            }
        })
    }

    get ordersEnabled(): boolean {
        return this.$currentCompany.orderSettings?.enabled || false
    }

    get availablePipelines(): Array<{ label: string, value: string }> {
        return this.pipelines.map(pipeline => ({
            label: pipeline.name,
            value: pipeline.id,
        }))
    }
}
